/**
 * My Characters page - styled like Explore page
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

function MyCharacters() {
  const { user, language } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadCharacters();
    }
  }, [user]);

  const loadCharacters = async () => {
    try {
      const response = await axios.get('/web/characters');
      console.log('Raw characters response:', response.data);
      // Ensure likes and comments are properly set
      const charactersWithStats = response.data.map(char => {
        // Extract likes - should be a number
        let likes = 0;
        if (typeof char.likes === 'number') {
          likes = char.likes;
        } else if (char.likes !== undefined && char.likes !== null) {
          likes = parseInt(char.likes) || 0;
        }
        
        // Extract comments - should be a string
        let comments = "0";
        if (char.comments !== undefined && char.comments !== null) {
          comments = String(char.comments);
        }
        
        console.log(`Character ${char.id}: likes=${likes} (type: ${typeof char.likes}, value: ${char.likes}), comments=${comments} (type: ${typeof char.comments}, value: ${char.comments})`);
        
        return {
          ...char,
          likes,
          comments
        };
      });
      console.log('Processed characters:', charactersWithStats);
      setCharacters(charactersWithStats);
    } catch (error) {
      console.error('Failed to load characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const translations = {
    en: {
      title: "My Characters",
      createNew: "Create New",
      likes: "likes",
      comments: "comments",
      chat: "Chat",
      noCharacters: "No characters yet. Create your first one!",
      loading: "Loading..."
    },
    ru: {
      title: "Мои персонажи",
      createNew: "Создать нового",
      likes: "лайков",
      comments: "комментариев",
      chat: "Чат",
      noCharacters: "Персонажей пока нет. Создайте первого!",
      loading: "Загрузка..."
    }
  };

  const t = translations[language] || translations.en;

  if (!user) {
    return (
      <div className="text-white text-center py-12">
        {language === 'en' ? 'Please login' : 'Пожалуйста, войдите'}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400">{t.loading}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">{t.title}</h1>
        <Link
          to="/web/create"
          className="px-4 py-2 bg-burgundy-600 hover:bg-burgundy-700 text-white rounded-lg transition"
        >
          {t.createNew}
        </Link>
      </div>

      {characters.length === 0 ? (
        <div className="text-gray-400 text-center py-12">{t.noCharacters}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {characters.map((character) => {
            const description = language === 'en' 
              ? (character.personality?.description_en || character.personality?.description || '')
              : (character.personality?.description || character.personality?.description_en || '');
            
            const age = character.appearance?.age || character.personality?.age || '';
            // Ensure likes and comments are properly extracted
            const likes = typeof character.likes === 'number' ? character.likes : (parseInt(character.likes) || 0);
            const comments = character.comments !== undefined && character.comments !== null ? String(character.comments) : "0";

            const handleCharacterClick = (e) => {
              // Don't navigate if clicking on edit button
              if (e.target.closest('.edit-button')) {
                return;
              }
              e.preventDefault();
              navigate(`/web/chat/${character.id}`);
            };

            const handleEditClick = (e) => {
              e.stopPropagation();
              navigate(`/web/characters/${character.id}/edit`);
            };

            return (
              <div
                key={character.id}
                onClick={handleCharacterClick}
                className="bg-dark-800 border border-burgundy-800 rounded-lg overflow-hidden hover:bg-dark-700 hover:border-burgundy-700 transition group cursor-pointer relative"
              >
                {/* Character Image with hover preview and overlay text */}
                <div className="relative aspect-[3/4] overflow-hidden group">
                  <img
                    src={character.avatar_url || '/avatars/default.png'}
                    alt={character.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={(e) => {
                      e.target.src = '/avatars/default.png';
                    }}
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-burgundy-900 bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center pointer-events-none">
                    <span className="text-white opacity-0 group-hover:opacity-100 font-semibold bg-burgundy-600 px-4 py-2 rounded">
                      {t.chat}
                    </span>
                  </div>
                  
                  {/* Character Info Overlay - positioned at bottom */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/60 to-transparent">
                    <div className="mb-2">
                      <h3 className="font-semibold text-lg text-white mb-1">
                        {character.name}
                        {age && age !== 'N/A' && (
                          <span className="text-sm text-white/80 ml-2">{age} {language === 'en' ? 'y.o.' : 'лет'}</span>
                        )}
                      </h3>
                    </div>

                    {/* Description */}
                    {description && (
                      <p className="text-sm text-white/90 mb-3" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {description.substring(0, 100)}
                        {description.length > 100 ? '...' : ''}
                      </p>
                    )}

                    {/* Engagement Stats */}
                    <div className="flex items-center gap-4 text-sm text-white/90 mb-2">
                      <span className="flex items-center gap-1">🤍 {likes || 0}</span>
                      <span className="flex items-center gap-1">💬 {comments || "0"}</span>
                      <span className="flex items-center gap-1">📷 {character.images_count ?? 0}</span>
                      <span className="flex items-center gap-1">🎥 {character.videos_count ?? 0}</span>
                    </div>
                    
                    {/* Edit Button and Status */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={handleEditClick}
                        className="edit-button px-3 py-1.5 bg-burgundy-600 hover:bg-burgundy-700 text-white text-xs font-semibold rounded transition"
                      >
                        {language === 'en' ? 'Edit' : 'Редактировать'}
                      </button>
                      
                      {/* Status */}
                      {character.is_public ? (
                        <span className="inline-block px-3 py-1.5 text-xs font-semibold rounded bg-green-600/20 text-green-400 border border-green-600/30">
                          {language === 'en' ? 'Public' : 'Публичный'}
                        </span>
                      ) : character.rejection_reason ? (
                        <span className="inline-block px-3 py-1.5 text-xs font-semibold rounded bg-red-600/20 text-red-400 border border-red-600/30">
                          {language === 'en' ? 'Rejected' : 'Отклонен'}
                        </span>
                      ) : (
                        <span className="inline-block px-3 py-1.5 text-xs font-semibold rounded bg-yellow-600/20 text-yellow-400 border border-yellow-600/30">
                          {language === 'en' ? 'Private' : 'Приватный'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MyCharacters;

